if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.isSubmitting = false;
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.isSubmitting || this.cartNeedsReview || this.submitButton.getAttribute('aria-disabled') === 'true') return;
        const cartTransactions = window.LashNovaCartTransactions;
        let cartLock = null;
        let submitButtons = [];
        let formData;
        let requestAttempted = false;
        let responseConfirmed = false;
        let definitiveRejection = false;
        let readbackAttempted = false;
        try {
          cartLock = cartTransactions?.tryAcquire({ kind: 'product-add' });
          if (cartTransactions && !cartLock) {
            this.handleErrorMessage(cartTransactions.text('busy'));
            return;
          }
          this.isSubmitting = true;
          this.lnAnalyticsOutcomeTracked = false;
          submitButtons = this.getSubmitButtons();
          this.handleErrorMessage();
          submitButtons.forEach((button) => {
            button.setAttribute('aria-disabled', true);
            button.classList.add('loading');
          });
          this.querySelector('.loading__spinner')?.classList.remove('hidden');
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          formData = new FormData(this.form);
          if (this.cart) {
            formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
          requestAttempted = true;
          const httpResponse = await fetch(routes.cart_add_url, config);
          const response = await httpResponse.json();
          if (!httpResponse.ok || response.status) {
            if (httpResponse.status === 422 && cartTransactions) {
              readbackAttempted = true;
              const currentCart = await cartTransactions.readCart();
              definitiveRejection = Array.isArray(currentCart.items) && !currentCart.items.some(
                (item) => Number(item.variant_id || item.id) === Number(formData.get('id'))
              );
            }
            if (!definitiveRejection) throw new Error(response.description || response.message || cartTransactions?.text('request_failed'));
            this.trackAnalyticsOutcome('error', formData, evt, response.description || response.message);
            try {
              Promise.resolve(publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              })).catch((error) => console.error('Cart error subscriber failed:', error));
            } catch (error) {
              console.error('Cart error subscriber failed:', error);
            }
            this.handleErrorMessage(response.description || response.message || window.cartStrings.error);
            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            this.error = Boolean(soldOutMessage);
            if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
            }
            return;
          }
          if (!response || (response.id === undefined && !Array.isArray(response.items))) throw new Error('Invalid add response');
          responseConfirmed = true;
          this.trackAnalyticsOutcome('success', formData, evt);
          if (!this.cart) {
            window.location.assign(window.routes.cart_url);
            return;
          }
          const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
          try {
            Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form', productVariantId: formData.get('id'), cartData: response,
            })).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            }).catch((error) => console.error('Cart update subscriber failed:', error));
          } catch (error) {
            console.error('Cart update subscriber failed:', error);
          }
          this.error = false;
          const renderCart = () => {
            try {
              CartPerformance.measure('add:paint-updated-sections', () => this.cart.renderContents(response, {
                variantId: Number(formData.get('id')),
              }));
            } catch (error) {
              console.error('Cart rendering failed:', error);
              this.showCartReview(cartTransactions);
              window.location.assign(window.routes.cart_url);
            }
          };
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) quickAddModal.hide(true);
          renderCart();
        } catch (error) {
          console.error(error);
          if (formData) this.trackAnalyticsOutcome('error', formData, evt, error);
          if (requestAttempted && !definitiveRejection) {
            this.showCartReview(cartTransactions);
            if (!readbackAttempted) {
              try { await cartTransactions?.readCart(); } catch (readError) { console.error('Cart readback failed:', readError); }
            }
            window.location.assign(window.routes.cart_url);
          } else {
            this.handleErrorMessage(cartTransactions?.text('request_failed') || window.cartStrings?.error);
          }
        } finally {
          try {
            if (!cartTransactions || cartLock) {
              this.isSubmitting = false;
              submitButtons.forEach((button) => {
                button.classList.remove('loading');
                if (!this.error && !this.cartNeedsReview) button.removeAttribute('aria-disabled');
              });
              if (responseConfirmed && this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              this.querySelector('.loading__spinner')?.classList.add('hidden');
              CartPerformance.measureFromEvent('add:user-action', evt);
            }
          } finally {
            cartTransactions?.release(cartLock);
          }
        }
      }

      showCartReview(cartTransactions) {
        this.cartNeedsReview = true;
        this.getSubmitButtons().forEach((button) => button.setAttribute('aria-disabled', true));
        const message = cartTransactions?.text('request_failed') || window.cartStrings?.error;
        const hideErrors = this.hideErrors;
        this.hideErrors = false;
        try { this.handleErrorMessage(message); } finally { this.hideErrors = hideErrors; }
        if (!this.errorMessageWrapper) {
          this.errorMessageWrapper = document.createElement('div');
          this.errorMessageWrapper.setAttribute('role', 'alert');
          this.errorMessageWrapper.dataset.lnCartRecovery = '';
          const errorMessage = document.createElement('span');
          errorMessage.textContent = message;
          this.errorMessageWrapper.appendChild(errorMessage);
          this.appendChild(this.errorMessageWrapper);
        }
        let reviewLink = this.errorMessageWrapper.querySelector('[data-ln-cart-review]');
        if (!reviewLink) {
          reviewLink = document.createElement('a');
          reviewLink.dataset.lnCartReview = '';
          this.errorMessageWrapper.appendChild(reviewLink);
        }
        reviewLink.href = window.routes.cart_url;
        reviewLink.textContent = window.LashNovaCartStrings?.review_cart || 'Review cart';
      }

      trackAnalyticsOutcome(outcome, formData, evt, error) {
        if (this.lnAnalyticsOutcomeTracked || !this.dataset.lnProductKind) return;
        this.lnAnalyticsOutcomeTracked = true;

        const analytics = window.LashNovaAnalytics;
        if (!analytics?.track) return;

        const kind = this.dataset.lnProductKind === 'bundle' ? 'bundle' : 'product';
        const quantity = Number(formData.get('quantity')) || 1;
        const price = Number(this.dataset.lnPrice) || 0;
        const sticky = evt?.submitter?.closest?.('[data-lnp-mobile-cart]');
        const surface = sticky
          ? `${kind === 'bundle' ? 'bundle' : 'single'}_pdp_sticky`
          : this.dataset.lnSurface || `${kind === 'bundle' ? 'bundle' : 'single'}_pdp_main`;

        try {
          analytics.track(`ln_${kind}_add_${outcome}`, {
            surface,
            product_id: Number(this.dataset.lnProductId) || undefined,
            product_handle: this.dataset.lnProductHandle,
            product_title: this.dataset.lnProductTitle,
            variant_id: Number(formData.get('id')) || undefined,
            quantity,
            value: price ? (price * quantity) / 100 : undefined,
            currency: this.dataset.lnCurrency,
            error_code: outcome === 'error' ? analytics.classifyError?.(error) : undefined,
          });
        } catch (_error) {
          // Analytics must never interrupt the authoritative cart response.
        }
      }

      getSubmitButtons() {
        const associatedSubmitButtons = Array.from(this.form.elements).filter(
          (element) => element.matches?.('button[type="submit"], input[type="submit"]')
        );

        return associatedSubmitButtons.length ? associatedSubmitButtons : [this.submitButton];
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper') ||
          this.closest('[data-lnp-root]')?.querySelector('[data-lnp-main-error]');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
